import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

export type ThemePreference = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

/** Keep in sync with the blocking script in `src/public/index.html`. */
export const THEME_STORAGE_KEY = 'appearance:theme:v1';
export const DEFAULT_THEME_PREFERENCE: ThemePreference = 'system';

type ThemeContextValue = {
  readonly theme: ThemePreference;
  readonly resolvedTheme: ResolvedTheme;
  readonly setTheme: (preference: ThemePreference) => void;
};

const ThemeContext = createContext<ThemeContextValue | null>(null);

export const parseThemePreference = (value: string | null): ThemePreference | null => {
  if (value === 'light' || value === 'dark' || value === 'system') {
    return value;
  }

  return null;
};

const getSystemIsDark = (): boolean =>
  window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false;

const readStoredTheme = (): ThemePreference | null => {
  try {
    return parseThemePreference(window.localStorage.getItem(THEME_STORAGE_KEY));
  } catch {
    return null;
  }
};

const writeStoredTheme = (preference: ThemePreference): void => {
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, preference);
  } catch {
    // Quota or private mode: keep the in-memory preference for this session.
  }
};

const applyResolvedTheme = (resolved: ResolvedTheme): void => {
  document.documentElement.classList.toggle('dark', resolved === 'dark');
};

type ThemeProviderProps = {
  readonly children: ReactNode;
  readonly defaultTheme?: ThemePreference;
};

export const ThemeProvider = ({
  children,
  defaultTheme = DEFAULT_THEME_PREFERENCE,
}: ThemeProviderProps) => {
  const [theme, setThemeState] = useState<ThemePreference>(() => readStoredTheme() ?? defaultTheme);
  const [systemIsDark, setSystemIsDark] = useState(getSystemIsDark);

  const setTheme = useCallback((preference: ThemePreference) => {
    setThemeState(preference);
    writeStoredTheme(preference);
  }, []);

  useEffect(() => {
    const mediaQuery = window.matchMedia?.('(prefers-color-scheme: dark)');
    if (!mediaQuery) {
      return;
    }

    const handler = () => {
      setSystemIsDark(mediaQuery.matches);
    };
    mediaQuery.addEventListener('change', handler);
    return () => {
      mediaQuery.removeEventListener('change', handler);
    };
  }, []);

  const resolvedTheme: ResolvedTheme =
    theme === 'system' ? (systemIsDark ? 'dark' : 'light') : theme;

  useLayoutEffect(() => {
    applyResolvedTheme(resolvedTheme);
  }, [resolvedTheme]);

  const value = useMemo(() => ({theme, resolvedTheme, setTheme}), [theme, resolvedTheme, setTheme]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = (): ThemeContextValue => {
  const value = useContext(ThemeContext);

  if (value === null) {
    throw new Error('useTheme must be used within ThemeProvider.');
  }

  return value;
};
